/**
 * The portfolio loans table — builds the TanStack Table instance from the loaded
 * rows and renders the kit `DataTable`. Sorting is server-side (`manualSorting`):
 * the table's sorting state is driven by the `sort`/`direction` props and clicks
 * flow back out through `onSortChange` (the page maps them to URL state, which
 * re-runs the query). Presentational and storyable — it takes already-loaded rows.
 */
import { type ReactNode, useMemo } from 'react';
import {
  getCoreRowModel,
  useReactTable,
  type SortingState,
  type OnChangeFn,
} from '@tanstack/react-table';
import {
  PORTFOLIO_SORT_KEYS,
  type PortfolioLoanRow,
  type PortfolioSortKey,
  type PortfolioSortDirection,
} from '@repo/shared/common';
import { DataTable } from '~/components/ui';
import { usePortfolioColumns } from './columns';

/** Narrow an arbitrary table column id to a known portfolio sort key. */
function isPortfolioSortKey(value: string): value is PortfolioSortKey {
  return (PORTFOLIO_SORT_KEYS as readonly string[]).includes(value);
}

export interface PortfolioTableProps {
  readonly rows: readonly PortfolioLoanRow[];
  readonly isLoading: boolean;
  readonly isFetchingNextPage: boolean;
  readonly onReachEnd: () => void;
  readonly sort: PortfolioSortKey;
  readonly direction: PortfolioSortDirection;
  readonly onSortChange: (sort: PortfolioSortKey, direction: PortfolioSortDirection) => void;
  readonly emptyState?: ReactNode;
  readonly ariaLabel?: string;
}

export function PortfolioTable({
  rows,
  isLoading,
  isFetchingNextPage,
  onReachEnd,
  sort,
  direction,
  onSortChange,
  emptyState,
  ariaLabel,
}: PortfolioTableProps): ReactNode {
  const columns = usePortfolioColumns();
  const data = useMemo(() => [...rows], [rows]);
  const sorting: SortingState = useMemo(
    () => [{ id: sort, desc: direction === 'desc' }],
    [sort, direction],
  );

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    // Only sortable columns (whose ids are valid sort keys) reach this; removal is
    // disabled so there is always exactly one sorted column.
    if (first && isPortfolioSortKey(first.id)) {
      onSortChange(first.id, first.desc ? 'desc' : 'asc');
    }
  };

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: handleSortingChange,
    manualSorting: true,
    enableSortingRemoval: false,
    enableMultiSort: false,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <DataTable
      table={table}
      onReachEnd={onReachEnd}
      isLoading={isLoading}
      isFetchingNextPage={isFetchingNextPage}
      emptyState={emptyState}
      ariaLabel={ariaLabel}
      data-testid="portfolio-table"
    />
  );
}
